import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { badgeClass, ConfirmDialog, Field, FormActions, formatMoney, formatNumber, getApiErrorMessage, inputClass, inputErrorClass, primaryButtonClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { formatMoment, thClass } from '../quality/partApprovalShared';

type Tab = 'import' | 'webhooks' | 'tally';
type ImportKind = 'products' | 'customers' | 'vendors';

interface ImportProblem {
  line: number;
  column: string | null;
  message: string;
}

interface ImportCheck {
  rows: number;
  valid: number;
  problems: ImportProblem[];
  sample: Record<string, unknown>[];
}

interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string[];
  isActive: boolean;
  secretHint: string;
  delivered: number;
  pending: number;
  failed: number;
  lastDeliveredAt: string | null;
}

interface Delivery {
  id: string;
  subscriptionName: string;
  event: string;
  status: 'pending' | 'delivered' | 'failed';
  attempts: number;
  nextAttemptAt: string | null;
  lastStatusCode: number | null;
  lastError: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

type TallySettings = Record<string, string>;

const KIND_LABEL: Record<ImportKind, string> = { products: 'Items', customers: 'Customers', vendors: 'Vendors' };

const TALLY_FIELDS: { key: string; label: string }[] = [
  { key: 'salesLedger', label: 'Sales ledger' },
  { key: 'purchaseLedger', label: 'Purchase ledger' },
  { key: 'otherChargesLedger', label: 'Other charges on vendor bills' },
  { key: 'outputCgstLedger', label: 'Output CGST' },
  { key: 'outputSgstLedger', label: 'Output SGST' },
  { key: 'outputIgstLedger', label: 'Output IGST' },
  { key: 'outputCessLedger', label: 'Output cess' },
  { key: 'inputCgstLedger', label: 'Input CGST' },
  { key: 'inputSgstLedger', label: 'Input SGST' },
  { key: 'inputIgstLedger', label: 'Input IGST' },
  { key: 'roundOffLedger', label: 'Round off' },
  { key: 'bankLedger', label: 'Bank (cheque, transfer, UPI)' },
  { key: 'cashLedger', label: 'Cash' },
  { key: 'debtorsGroup', label: 'Group for customers' },
  { key: 'creditorsGroup', label: 'Group for vendors' },
  { key: 'salesVoucherType', label: 'Sales voucher type' },
  { key: 'purchaseVoucherType', label: 'Purchase voucher type' },
  { key: 'receiptVoucherType', label: 'Receipt voucher type' },
  { key: 'paymentVoucherType', label: 'Payment voucher type' },
];

const DELIVERY_TONE: Record<Delivery['status'], string> = {
  delivered: 'bg-green-50 text-green-700 ring-green-600/20',
  pending: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  failed: 'bg-red-50 text-red-700 ring-red-600/20',
};

// The file the server sends, saved under the name it gives
const download = async (url: string, params: Record<string, string> | undefined, fallback: string) => {
  const response = await api.get(url, { params, responseType: 'blob' });
  const named = /filename="?([^";]+)"?/.exec(String(response.headers['content-disposition'] ?? ''));
  const link = document.createElement('a');
  link.href = URL.createObjectURL(response.data as Blob);
  link.download = named?.[1] ?? fallback;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
};

const blobMessage = async (error: unknown, fallback: string) => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()).message || fallback;
    } catch {
      return fallback;
    }
  }
  return getApiErrorMessage(error, fallback);
};

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const firstOfMonth = () => `${today().slice(0, 8)}01`;

// ---------------------------------------------------------------- import

const ImportPanel: React.FC<{ canImport: boolean }> = ({ canImport }) => {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<ImportKind>('products');
  const [file, setFile] = useState<File | null>(null);
  const [checked, setChecked] = useState<ImportCheck | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const { data: columns = [] } = useQuery<{ key: string; required: boolean; hint: string }[]>({
    queryKey: ['import-columns', kind],
    queryFn: async () => (await api.get(`/integrations/import/${kind}/columns`)).data,
  });

  const form = () => {
    const data = new FormData();
    if (file) data.append('file', file);
    return data;
  };
  const reset = () => {
    setFile(null);
    setChecked(null);
    setInputKey((value) => value + 1);
  };
  const check = useMutation({
    mutationFn: async () => (await api.post(`/integrations/import/${kind}/check`, form(), { headers: { 'Content-Type': 'multipart/form-data' } })).data as ImportCheck,
    onSuccess: (result) => setChecked(result),
    onError: (error) => {
      setChecked(null);
      toast.error(getApiErrorMessage(error, 'The file could not be read'), { duration: 8000 });
    },
  });
  const commit = useMutation({
    mutationFn: async () => (await api.post(`/integrations/import/${kind}`, form(), { headers: { 'Content-Type': 'multipart/form-data' } })).data as { created: number },
    onSuccess: (result) => {
      toast.success(`${result.created} ${KIND_LABEL[kind].toLowerCase()} imported`);
      reset();
      queryClient.invalidateQueries();
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Nothing was imported'), { duration: 8000 }),
  });

  return (
    <div className="space-y-5 p-4">
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="What to import">
        {(Object.keys(KIND_LABEL) as ImportKind[]).map((value) => (
          <button key={value} type="button" role="radio" aria-checked={kind === value} onClick={() => { setKind(value); reset(); }} className={cn('rounded-md px-4 py-2 text-sm font-medium ring-1 ring-inset', kind === value ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50')}>
            {KIND_LABEL[value]}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-1">
          <p className="text-sm text-gray-600">Start from the template, fill it in a spreadsheet and save it as <span className="font-medium">CSV UTF-8</span>. The file is checked first; it is imported only when every row is right, and then all at once.</p>
          <button type="button" onClick={() => download(`/integrations/import/${kind}/template`, undefined, `${kind}-import-template.csv`).catch(async (error) => toast.error(await blobMessage(error, 'The template could not be downloaded')))} className={secondaryButtonClass}>
            <ArrowDownTrayIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Download Template
          </button>
          <div className="rounded-md ring-1 ring-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <tbody className="divide-y divide-gray-100">
                {columns.map((column) => (
                  <tr key={column.key}>
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono text-gray-900">{column.key}{column.required && <span className="text-red-500">*</span>}</td>
                    <td className="px-3 py-1.5 text-gray-600">{column.hint}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-4 lg:col-span-2">
          <Field label={`${KIND_LABEL[kind]} file`} htmlFor="import-file" hint="CSV, up to 5 MB and 5,000 rows">
            <input key={inputKey} id="import-file" type="file" accept=".csv,text/csv" onChange={(event) => { setFile(event.target.files?.[0] ?? null); setChecked(null); }} className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-indigo-700 hover:file:bg-indigo-100" />
          </Field>
          <div className="flex gap-3">
            <button type="button" onClick={() => check.mutate()} disabled={!file || !canImport || check.isPending} className={secondaryButtonClass}>{check.isPending ? 'Checking…' : 'Check File'}</button>
            <button type="button" data-testid="import-commit" onClick={() => commit.mutate()} disabled={!checked || checked.problems.length > 0 || checked.valid === 0 || !canImport || commit.isPending} className={primaryButtonClass}>
              <ArrowUpTrayIcon className="-ml-0.5 mr-2 h-4 w-4" />
              {commit.isPending ? 'Importing…' : checked && checked.problems.length === 0 ? `Import ${formatNumber(checked.valid)} ${KIND_LABEL[kind].toLowerCase()}` : 'Import'}
            </button>
          </div>
          {!canImport && <p className="text-sm text-gray-500">Your role may look at this page but not import.</p>}

          {checked && (
            <div className="space-y-3" data-testid="import-result">
              <p className={cn('text-sm font-medium', checked.problems.length ? 'text-red-700' : 'text-green-700')}>
                {checked.problems.length
                  ? `${formatNumber(checked.rows)} rows, ${formatNumber(checked.problems.length)} problem${checked.problems.length === 1 ? '' : 's'}. Put them right in the file and check it again; nothing is imported until every row is right.`
                  : `${formatNumber(checked.rows)} rows, all right. Nothing is saved until you import.`}
              </p>
              {checked.problems.length > 0 && (
                <div className="max-h-80 overflow-y-auto rounded-md ring-1 ring-red-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-red-50">
                      <tr><th className={thClass}>Line</th><th className={thClass}>Column</th><th className={thClass}>Problem</th></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {checked.problems.slice(0, 200).map((problem, index) => (
                        <tr key={`${problem.line}-${problem.column}-${index}`} data-testid="import-problem">
                          <td className="px-4 py-2 tabular-nums text-gray-900">{problem.line}</td>
                          <td className="px-4 py-2 font-mono text-gray-700">{problem.column ?? '—'}</td>
                          <td className="px-4 py-2 text-gray-700">{problem.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {checked.problems.length === 0 && checked.sample.length > 0 && (
                <div className="overflow-x-auto rounded-md ring-1 ring-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-50">
                      <tr>{Object.keys(checked.sample[0]).slice(0, 8).map((key) => (<th key={key} className="px-3 py-2 text-left font-semibold text-gray-500">{key}</th>))}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {checked.sample.slice(0, 10).map((row, index) => (
                        <tr key={index}>{Object.keys(checked.sample[0]).slice(0, 8).map((key) => (<td key={key} className="whitespace-nowrap px-3 py-1.5 text-gray-700">{String(row[key] ?? '')}</td>))}</tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- webhooks

const EVENT_HINT = '"*" for everything except sign-ins; "sales.*" for one area; "*.delete" for one kind of action; "purchase.create" for one of each. Separate with commas.';

const WebhookForm: React.FC<{ webhook: Webhook | null; isPending: boolean; onCancel: () => void; onSubmit: (body: { name: string; url: string; events: string[]; isActive: boolean }) => void }> = ({ webhook, isPending, onCancel, onSubmit }) => {
  const [name, setName] = useState(webhook?.name ?? '');
  const [url, setUrl] = useState(webhook?.url ?? '');
  const [events, setEvents] = useState(webhook?.events.join(', ') ?? '*');
  const [isActive, setIsActive] = useState(webhook?.isActive ?? true);
  const [asked, setAsked] = useState(false);
  const list = events.split(',').map((event) => event.trim()).filter(Boolean);
  const errors = {
    name: name.trim().length < 2 ? 'Name the subscriber' : undefined,
    url: !/^https?:\/\/\S+$/i.test(url.trim()) ? 'An address starting with http:// or https://' : undefined,
    events: list.length === 0 ? 'At least one event' : list.some((event) => !/^(\*|[a-z_]+)\.(\*|[a-z_]+)$|^\*$/.test(event)) ? 'Each event is "*", "area.*", "*.action" or "area.action"' : undefined,
  };
  return (
    <form
      noValidate
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        setAsked(true);
        if (!errors.name && !errors.url && !errors.events) onSubmit({ name: name.trim(), url: url.trim(), events: list, isActive });
      }}
    >
      <Field label="Name" htmlFor="wh-name" required error={asked ? errors.name : undefined}>
        <input id="wh-name" type="text" maxLength={100} value={name} onChange={(event) => setName(event.target.value)} placeholder="Warehouse app" className={cn(inputClass, asked && errors.name && inputErrorClass)} />
      </Field>
      <Field label="Address" htmlFor="wh-url" required error={asked ? errors.url : undefined} hint="The other system's address that takes the POST">
        <input id="wh-url" type="url" maxLength={500} value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://example.com/hooks/rightfit" className={cn(inputClass, asked && errors.url && inputErrorClass)} />
      </Field>
      <Field label="Events" htmlFor="wh-events" required error={asked ? errors.events : undefined} hint={EVENT_HINT}>
        <input id="wh-events" type="text" value={events} onChange={(event) => setEvents(event.target.value)} className={cn(inputClass, 'font-mono', asked && errors.events && inputErrorClass)} />
      </Field>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
        Active
      </label>
      <FormActions>
        <button type="button" onClick={onCancel} disabled={isPending} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>{isPending ? 'Saving…' : webhook ? 'Save Changes' : 'Add Webhook'}</button>
      </FormActions>
    </form>
  );
};

const WebhooksPanel: React.FC<{ canEdit: boolean; canRun: boolean }> = ({ canEdit, canRun }) => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Webhook | null | 'new'>(null);
  const [secret, setSecret] = useState<{ name: string; secret: string } | null>(null);
  const [removing, setRemoving] = useState<Webhook | null>(null);
  const [filter, setFilter] = useState('');
  const { data: hooks = [] } = useQuery<Webhook[]>({ queryKey: ['webhooks'], queryFn: async () => (await api.get('/integrations/webhooks')).data });
  const { data: deliveries } = useQuery<{ data: Delivery[]; total: number }>({
    queryKey: ['webhook-deliveries', filter],
    queryFn: async () => (await api.get('/integrations/webhook-deliveries', { params: { limit: 25, ...(filter ? { status: filter } : {}) } })).data,
    refetchInterval: 15000,
  });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    queryClient.invalidateQueries({ queryKey: ['webhook-deliveries'] });
  };
  const failed = (fallback: string) => (error: unknown) => toast.error(getApiErrorMessage(error, fallback), { duration: 8000 });

  const save = useMutation({
    mutationFn: async (body: { name: string; url: string; events: string[]; isActive: boolean }) =>
      editing && editing !== 'new' ? (await api.put(`/integrations/webhooks/${editing.id}`, body)).data : (await api.post('/integrations/webhooks', body)).data,
    onSuccess: (saved: { name: string; secret?: string }) => {
      if (saved.secret) setSecret({ name: saved.name, secret: saved.secret });
      else toast.success('Webhook saved');
      setEditing(null);
      refresh();
    },
    onError: failed('Could not save the webhook'),
  });
  const test = useMutation({
    mutationFn: async (hook: Webhook) => (await api.post(`/integrations/webhooks/${hook.id}/test`)).data as Delivery,
    onSuccess: (result) => {
      if (result.status === 'delivered') toast.success(`The address answered ${result.lastStatusCode}`);
      else toast.error(result.lastError ?? 'The ping did not go through', { duration: 8000 });
      refresh();
    },
    onError: failed('Could not send the ping'),
  });
  const rotate = useMutation({
    mutationFn: async (hook: Webhook) => ({ name: hook.name, ...((await api.post(`/integrations/webhooks/${hook.id}/rotate-secret`)).data as { secret: string }) }),
    onSuccess: (result) => {
      setSecret(result);
      refresh();
    },
    onError: failed('Could not make a new secret'),
  });
  const remove = useMutation({
    mutationFn: async (hook: Webhook) => api.delete(`/integrations/webhooks/${hook.id}`),
    onSuccess: () => {
      toast.success('Webhook removed');
      setRemoving(null);
      refresh();
    },
    onError: failed('Could not remove the webhook'),
  });
  const redeliver = useMutation({
    mutationFn: async (delivery: Delivery) => (await api.post(`/integrations/webhook-deliveries/${delivery.id}/redeliver`)).data as Delivery,
    onSuccess: (result) => {
      if (result.status === 'delivered') toast.success('Delivered');
      else toast.error(result.lastError ?? 'Still not delivered', { duration: 8000 });
      refresh();
    },
    onError: failed('Could not send again'),
  });

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-3xl text-sm text-gray-600">
          Other systems are told what happens here: every entry of the audit trail is an event such as <span className="font-mono">purchase.create</span>. Each delivery is a POST of JSON, signed with the webhook&apos;s secret in <span className="font-mono">X-RightFit-Signature</span> (HMAC-SHA256 of the timestamp, a dot and the body). One that fails is tried again five times over about eight hours.
        </p>
        {canEdit && (
          <button type="button" onClick={() => setEditing('new')} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Add Webhook
          </button>
        )}
      </div>

      {hooks.length === 0 ? (
        <p className="rounded-md bg-gray-50 p-6 text-center text-sm text-gray-500">No webhooks yet.</p>
      ) : (
        <div className="space-y-3">
          {hooks.map((hook) => (
            <div key={hook.id} className="rounded-md p-4 ring-1 ring-gray-200" data-testid="webhook">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">
                    {hook.name}
                    {!hook.isActive && <span className={cn(badgeClass, 'ml-2 bg-gray-100 text-gray-600')}>Off</span>}
                  </p>
                  <p className="break-all font-mono text-xs text-gray-600">{hook.url}</p>
                  <p className="mt-1 text-xs text-gray-500">Events: <span className="font-mono">{hook.events.join(', ')}</span> · Secret {hook.secretHint}</p>
                  <p className="mt-1 text-xs text-gray-500">{hook.delivered} delivered · {hook.pending} waiting · {hook.failed} failed{hook.lastDeliveredAt ? ` · last ${formatMoment(hook.lastDeliveredAt)}` : ''}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {canRun && <button type="button" onClick={() => test.mutate(hook)} disabled={test.isPending} className={secondaryButtonClass}>Send Test</button>}
                  {canEdit && <button type="button" onClick={() => setEditing(hook)} className={secondaryButtonClass}>Edit</button>}
                  {canEdit && <button type="button" onClick={() => rotate.mutate(hook)} className={secondaryButtonClass}>New Secret</button>}
                  {canEdit && <button type="button" onClick={() => setRemoving(hook)} className="rounded-md px-3 py-2 text-sm font-semibold text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-50">Remove</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Deliveries</h3>
          <select value={filter} onChange={(event) => setFilter(event.target.value)} aria-label="Delivery status" className={cn(inputClass, 'w-40')}>
            <option value="">All</option>
            <option value="pending">Waiting</option>
            <option value="failed">Failed</option>
            <option value="delivered">Delivered</option>
          </select>
        </div>
        <div className="mt-2 overflow-x-auto rounded-md ring-1 ring-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr><th className={thClass}>When</th><th className={thClass}>Webhook</th><th className={thClass}>Event</th><th className={thClass}>How it went</th><th className={thClass}><span className="sr-only">Actions</span></th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {(deliveries?.data ?? []).length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">Nothing sent yet.</td></tr>
              ) : (
                deliveries!.data.map((delivery) => (
                  <tr key={delivery.id} data-testid="delivery">
                    <td className="whitespace-nowrap px-4 py-2 text-gray-600">{formatMoment(delivery.createdAt)}</td>
                    <td className="px-4 py-2 text-gray-900">{delivery.subscriptionName}</td>
                    <td className="px-4 py-2 font-mono text-gray-700">{delivery.event}</td>
                    <td className="px-4 py-2">
                      <span className={cn(badgeClass, 'ring-1 ring-inset', DELIVERY_TONE[delivery.status])}>{delivery.status === 'pending' ? 'Waiting' : delivery.status === 'failed' ? 'Failed' : 'Delivered'}</span>
                      <span className="ml-2 text-xs text-gray-500">
                        {delivery.attempts} tr{delivery.attempts === 1 ? 'y' : 'ies'}
                        {delivery.lastStatusCode ? ` · answered ${delivery.lastStatusCode}` : ''}
                        {delivery.status === 'pending' && delivery.nextAttemptAt ? ` · next ${formatMoment(delivery.nextAttemptAt)}` : ''}
                      </span>
                      {delivery.lastError && delivery.status !== 'delivered' && <span className="block text-xs text-red-600">{delivery.lastError}</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      {delivery.status === 'failed' && canRun && <button type="button" onClick={() => redeliver.mutate(delivery)} className="text-sm font-medium text-indigo-600 hover:text-indigo-800">Send Again</button>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={editing !== null} onClose={() => !save.isPending && setEditing(null)} title={editing && editing !== 'new' ? `Edit ${editing.name}` : 'Add Webhook'} size="lg">
        {editing !== null && <WebhookForm webhook={editing === 'new' ? null : editing} isPending={save.isPending} onCancel={() => setEditing(null)} onSubmit={(body) => save.mutate(body)} />}
      </Modal>
      <Modal isOpen={!!secret} onClose={() => setSecret(null)} title="Signing secret" size="lg">
        {secret && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">The secret of <span className="font-medium">{secret.name}</span>. Copy it into the other system now: it is not shown again.</p>
            <input readOnly value={secret.secret} onFocus={(event) => event.target.select()} aria-label="Signing secret" className={cn(inputClass, 'font-mono')} data-testid="webhook-secret" />
            <FormActions>
              <button type="button" onClick={() => navigator.clipboard?.writeText(secret.secret).then(() => toast.success('Copied'), () => undefined)} className={secondaryButtonClass}>Copy</button>
              <button type="button" onClick={() => setSecret(null)} className={primaryButtonClass}>Done</button>
            </FormActions>
          </div>
        )}
      </Modal>
      <ConfirmDialog isOpen={!!removing} onClose={() => setRemoving(null)} onConfirm={() => removing && remove.mutate(removing)} title={`Remove ${removing?.name ?? ''}`} message="Nothing more is sent to it, and its delivery log is removed." confirmLabel="Remove" pendingLabel="Removing…" isPending={remove.isPending} tone="danger" />
    </div>
  );
};

// ---------------------------------------------------------------- Tally

const TallyPanel: React.FC<{ canEdit: boolean; canExport: boolean }> = ({ canEdit, canExport }) => {
  const queryClient = useQueryClient();
  const { data: saved } = useQuery<TallySettings>({ queryKey: ['tally-settings'], queryFn: async () => (await api.get('/integrations/tally/settings')).data });
  const [settings, setSettings] = useState<TallySettings>({});
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [asked, setAsked] = useState(false);
  useEffect(() => {
    if (saved) setSettings(saved);
  }, [saved]);

  const days = from && to ? (new Date(to).getTime() - new Date(from).getTime()) / 86400000 : 0;
  const rangeProblem = !from || !to ? 'Choose both days' : from > to ? 'The first day is after the last day.' : days > 366 ? 'Export at most one year at a time.' : null;
  const rangeValid = !rangeProblem;
  const { data: summary, isFetching, error } = useQuery<{ ledgers: number; vouchers: { type: string; count: number; total: number }[]; warnings: string[] }>({
    queryKey: ['tally-summary', from, to],
    queryFn: async () => (await api.get('/integrations/tally/summary', { params: { from, to } })).data,
    enabled: rangeValid,
  });
  const empty = TALLY_FIELDS.filter((field) => !(settings[field.key] ?? '').trim());
  const save = useMutation({
    mutationFn: async () => (await api.put('/integrations/tally/settings', Object.fromEntries(TALLY_FIELDS.map((field) => [field.key, (settings[field.key] ?? '').trim()])))).data,
    onSuccess: () => {
      toast.success('Tally names saved');
      queryClient.invalidateQueries({ queryKey: ['tally-settings'] });
      queryClient.invalidateQueries({ queryKey: ['tally-summary'] });
    },
    onError: (cause) => toast.error(getApiErrorMessage(cause, 'Could not save the names'), { duration: 8000 }),
  });
  const exportFile = useMutation({
    mutationFn: async () => download('/integrations/tally/export', { from, to }, `Tally_${from}_${to}.xml`),
    onSuccess: () => toast.success('Tally file downloaded'),
    onError: async (cause) => toast.error(await blobMessage(cause, 'The export could not be made'), { duration: 8000 }),
  });
  const changed = saved && TALLY_FIELDS.some((field) => (saved[field.key] ?? '') !== (settings[field.key] ?? ''));
  const vouchers = summary?.vouchers ?? [];

  return (
    <div className="grid grid-cols-1 gap-6 p-4 xl:grid-cols-2">
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-900">Export</h3>
        <p className="text-sm text-gray-600">Sales invoices, vendor bills, customer receipts and vendor payments of the days chosen, with the customer and vendor ledgers they need, in the XML that Tally&apos;s Import Data reads. Cancelled documents are left out.</p>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="From" htmlFor="tally-from"><input id="tally-from" type="date" value={from} max={to || undefined} onChange={(event) => setFrom(event.target.value)} className={inputClass} /></Field>
          <Field label="To" htmlFor="tally-to"><input id="tally-to" type="date" value={to} min={from || undefined} onChange={(event) => setTo(event.target.value)} className={inputClass} /></Field>
        </div>
        {rangeProblem && <p className="text-sm text-red-600">{rangeProblem}</p>}
        {error ? <p className="text-sm text-red-600">{getApiErrorMessage(error, 'The export could not be worked out')}</p> : null}
        <div className="rounded-md ring-1 ring-gray-200" data-testid="tally-summary">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50"><tr><th className={thClass}>Vouchers</th><th className={cn(thClass, 'text-right')}>Count</th><th className={cn(thClass, 'text-right')}>Total</th></tr></thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {vouchers.length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-4 text-center text-gray-500">{isFetching ? 'Working out…' : 'Nothing in these days'}</td></tr>
              ) : (
                vouchers.map((voucher) => (
                  <tr key={voucher.type}><td className="px-4 py-2 text-gray-900">{voucher.type}</td><td className="px-4 py-2 text-right tabular-nums">{voucher.count}</td><td className="px-4 py-2 text-right tabular-nums">{formatMoney(voucher.total)}</td></tr>
                ))
              )}
              {summary && summary.ledgers > 0 && (
                <tr><td className="px-4 py-2 text-gray-600">Customer and vendor ledgers</td><td className="px-4 py-2 text-right tabular-nums">{summary.ledgers}</td><td /></tr>
              )}
            </tbody>
          </table>
        </div>
        {summary && summary.warnings.length > 0 && (
          <ul className="space-y-1 rounded-md bg-amber-50 p-3 text-sm text-amber-800" data-testid="tally-warnings">
            {summary.warnings.map((warning) => (<li key={warning}>{warning}</li>))}
          </ul>
        )}
        <button type="button" onClick={() => exportFile.mutate()} disabled={!canExport || !rangeValid || vouchers.length === 0 || exportFile.isPending} className={primaryButtonClass}>
          <ArrowDownTrayIcon className="-ml-0.5 mr-2 h-4 w-4" />
          {exportFile.isPending ? 'Making the file…' : 'Download Tally XML'}
        </button>
        <p className="text-xs text-gray-500">In Tally: Gateway of Tally → Import → Vouchers (or Masters and Vouchers), and choose the file. The ledgers on the right must exist in the company in Tally with exactly these names.</p>
      </section>

      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-900">Names in Tally</h3>
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            setAsked(true);
            if (empty.length === 0) save.mutate();
          }}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {TALLY_FIELDS.map((field) => (
              <Field key={field.key} label={field.label} htmlFor={`tally-${field.key}`} error={asked && !(settings[field.key] ?? '').trim() ? 'Enter the name' : undefined}>
                <input id={`tally-${field.key}`} type="text" maxLength={100} disabled={!canEdit} value={settings[field.key] ?? ''} onChange={(event) => setSettings((previous) => ({ ...previous, [field.key]: event.target.value }))} className={cn(inputClass, asked && !(settings[field.key] ?? '').trim() && inputErrorClass)} />
              </Field>
            ))}
          </div>
          {canEdit && (
            <FormActions>
              <button type="submit" disabled={save.isPending || !changed} className={primaryButtonClass}>{save.isPending ? 'Saving…' : 'Save Names'}</button>
            </FormActions>
          )}
        </form>
      </section>
    </div>
  );
};

const Integrations: React.FC = () => {
  const { can } = usePermissions();
  const [tab, setTab] = useState<Tab>('import');
  const tabs: { key: Tab; label: string }[] = [
    { key: 'import', label: 'Import' },
    { key: 'webhooks', label: 'Webhooks' },
    { key: 'tally', label: 'Tally' },
  ];
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Integrations</h1>
        <p className="mt-1 text-sm text-gray-500">Bring in items, customers and vendors from spreadsheets, tell other systems what happens here, and take the accounts to Tally.</p>
      </div>
      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex gap-1 border-b border-gray-200 px-4 pt-3" role="tablist">
          {tabs.map((item) => (
            <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} onClick={() => setTab(item.key)} className={cn('-mb-px border-b-2 px-3 py-2 text-sm font-medium', tab === item.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>{item.label}</button>
          ))}
        </div>
        {tab === 'import' && <ImportPanel canImport={can('integrations.execute')} />}
        {tab === 'webhooks' && <WebhooksPanel canEdit={can('integrations.update') && can('integrations.create')} canRun={can('integrations.execute')} />}
        {tab === 'tally' && <TallyPanel canEdit={can('integrations.update')} canExport={can('integrations.export')} />}
      </div>
    </div>
  );
};

export default Integrations;
